import React, { useState, useEffect, useCallback } from "react";
import { useAppDispatch, useAppSelector } from "../redux/store";
import { Box, Grid, Typography, Pagination, Stack } from "@mui/material";

import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import PageContainer from "../layouts/PageContainer";
import PageToolbar from "../components/common/PageToolbar";
import AppButton from "../components/buttons/AppButton";
import ConfirmDialog from "../components/common/ConfirmDialog";
import AccountCard from "../components/accounts/AccountCard";
import CreateAccountModal from "../components/accounts/CreateAccountModal";
import UpdateAccountModal from "../components/accounts/UpdateAccountModal";
import AppCircularProgress from "../components/common/AppCircularProgress";

import useDebounce from "../hooks/useDebounce";
import { usePermissions } from "../hooks/usePermissions";
import {
  getAccounts,
  deleteAccount,
  resetAccountServerStatus,
} from "../redux/actions/accountActions";
import PageError from "../components/common/PageError";
import { AccountResponseDto } from "../shared/dtos/account/AccountResponseDto";

const AccountsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { hasPermission } = usePermissions();

  // Redux State
  const {
    items: accounts,
    pagination,
    loadingFetchAll: loadingAccounts,
    errorFetchAll: errorLoadingAccounts,
    serverStatus,
    loadingDelete: loadingAccountDelete,
    loadingUpdate: loadingAccountUpdate,
    loadingCreate: loadingAccountCreate,
    errorCreate: errorCreatingAccount,
    errorUpdate: errorUpdatingAccount,
  } = useAppSelector((state) => state.account);

  // Local State
  const [page, setPage] = useState<number>(1);
  const limit = 12;
  const [searchTerm, setSearchTerm] = useState<string>("");
  const debouncedSearch = useDebounce(searchTerm, 500);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [selectedAccount, setSelectedAccount] =
    useState<AccountResponseDto | null>(null);

  // Permissions
  const canCreate = hasPermission("account:create");
  const canUpdate = hasPermission("account:update");
  const canDelete = hasPermission("account:delete");

  // Reset page to 1 when search term changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  // Fetch Data
  const fetchAccounts = useCallback(() => {
    dispatch(getAccounts({ page, size: limit, search: debouncedSearch }));
  }, [dispatch, page, limit, debouncedSearch]);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  // Handlers
  const handlePageChange = (
    _event: React.ChangeEvent<unknown>,
    value: number,
  ): void => {
    setPage(value);
  };

  const handleOpenCreate = (): void => setIsCreateModalOpen(true);
  const handleCloseCreate = useCallback(
    () => !loadingAccountCreate && setIsCreateModalOpen(false),
    [loadingAccountCreate],
  );

  const handleOpenUpdate = (account: AccountResponseDto): void => {
    setSelectedAccount(account);
    setIsUpdateModalOpen(true);
  };
  const handleCloseUpdate = useCallback(() => {
    if (!loadingAccountUpdate) {
      setIsUpdateModalOpen(false);
      setSelectedAccount(null);
    }
  }, [loadingAccountUpdate]);

  const handleOpenDelete = (account: AccountResponseDto): void => {
    setSelectedAccount(account);
    setIsDeleteModalOpen(true);
  };
  const handleCloseDelete = useCallback(() => {
    if (!loadingAccountDelete) {
      setIsDeleteModalOpen(false);
      setSelectedAccount(null);
    }
  }, [loadingAccountDelete]);

  const handleDeleteConfirm = (): void => {
    if (selectedAccount) dispatch(deleteAccount(selectedAccount.id));
  };

  // Auto Close on Success
  useEffect(() => {
    if (!serverStatus) return;
    const { operation, status } = serverStatus;
    if (operation === "create" && (status === 201 || status === 200))
      handleCloseCreate();
    if (operation === "update" && status === 200) handleCloseUpdate();
    if (operation === "delete" && status === 200) handleCloseDelete();
    dispatch(resetAccountServerStatus());
  }, [
    serverStatus,
    dispatch,
    handleCloseCreate,
    handleCloseUpdate,
    handleCloseDelete,
  ]);

  const renderContent = (): React.JSX.Element => {
    if (loadingAccounts) return <AppCircularProgress />;

    if (errorLoadingAccounts)
      return <PageError error={errorLoadingAccounts} onRetry={fetchAccounts} />;

    if (!accounts || accounts.length === 0)
      return (
        <Box sx={{ textAlign: "center", mt: 5 }}>
          <Typography>No Accounts Found</Typography>
        </Box>
      );

    return (
      <>
        <Grid container spacing={3}>
          {accounts.map((account) => (
            <Grid key={account.id} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
              <AccountCard
                account={account}
                onEdit={canUpdate ? () => handleOpenUpdate(account) : undefined}
                onDelete={
                  canDelete ? () => handleOpenDelete(account) : undefined
                }
              />
            </Grid>
          ))}
        </Grid>

        {/* Pagination Control */}
        {pagination && pagination.totalPages! > 1 && (
          <Stack sx={{ alignItems: "center", mt: 4 }}>
            <Pagination
              count={pagination.totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
              showFirstButton
              showLastButton
            />
          </Stack>
        )}
      </>
    );
  };

  return (
    <PageContainer
      title="Chart of Accounts"
      subtitle="Manage your assets, liabilities, equity, revenue, and expenses."
      actions={
        canCreate && (
          <AppButton
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            onClick={handleOpenCreate}
          >
            Add Account
          </AppButton>
        )
      }
    >
      <PageToolbar
        searchTerm={searchTerm}
        onSearchChange={(e) => setSearchTerm(e.target.value)}
        searchPlaceholder="Search accounts..."
      />

      {renderContent()}

      {isCreateModalOpen && (
        <CreateAccountModal
          open={isCreateModalOpen}
          onClose={handleCloseCreate}
          loading={loadingAccountCreate}
          error={errorCreatingAccount}
        />
      )}

      {isUpdateModalOpen && selectedAccount && (
        <UpdateAccountModal
          open={isUpdateModalOpen}
          onClose={handleCloseUpdate}
          account={selectedAccount}
          loading={loadingAccountUpdate}
          error={errorUpdatingAccount}
        />
      )}

      {isDeleteModalOpen && selectedAccount && (
        <ConfirmDialog
          open={isDeleteModalOpen}
          onClose={handleCloseDelete}
          onConfirm={handleDeleteConfirm}
          title="Delete Account"
          description={`Are you sure you want to delete account [${selectedAccount.accountCode}] ${selectedAccount.name}?`}
          loading={loadingAccountDelete}
        />
      )}
    </PageContainer>
  );
};

export default AccountsPage;
